import { useEffect, useState } from 'react';

// Use data attribute for cross-widget communication
const PORTAL_SELECTOR = '[data-portal="ws-locations-facets"]';

/**
 * Checks if an element is visible in the DOM
 * @param {Element} element
 * @returns {boolean}
 */
const isElementVisible = (element) => {
	// Use checkVisibility() if available (modern browsers)
	if (typeof element.checkVisibility === 'function') {
		return element.checkVisibility();
	}
	// Fallback: use getComputedStyle
	const style = getComputedStyle(element);
	return style.visibility !== 'hidden' && style.display !== 'none';
};

/**
 * Gets the visible portal container element
 * @returns {Element|null} The portal container if visible, null otherwise
 */
const getVisiblePortalContainer = () => {
	const container = document.querySelector(PORTAL_SELECTOR);
	if (!container) return null;
	return isElementVisible(container) ? container : null;
};

/**
 * Checks if any mutation involves a portal-related attribute change
 * @param {MutationRecord[]} mutations
 * @returns {boolean}
 */
const hasPortalAttributeChange = (mutations) =>
	mutations.some(
		(mutation) =>
			mutation.type === 'attributes' &&
			mutation.attributeName === 'data-portal'
	);

/**
 * Checks if any mutation involves adding/removing portal-related nodes
 * @param {MutationRecord[]} mutations
 * @returns {boolean}
 */
const hasRelevantChildListChange = (mutations) =>
	mutations.some(
		(mutation) =>
			mutation.type === 'childList' &&
			[...mutation.addedNodes, ...mutation.removedNodes].some(
				(node) =>
					node.nodeType === Node.ELEMENT_NODE &&
					(node.matches?.(PORTAL_SELECTOR) ||
						node.querySelector?.(PORTAL_SELECTOR))
			)
	);

/**
 * Hook that observes the DOM for portal container changes and returns the current container
 * @returns {Element|null} The current visible portal container
 */
export const usePortalContainer = () => {
	const [portalContainer, setPortalContainer] = useState(null);

	useEffect(() => {
		const updatePortalContainer = () => {
			const container = getVisiblePortalContainer();
			setPortalContainer(container);
		};

		// Initial check
		updatePortalContainer();

		// Observe DOM changes for portal container additions/removals
		const observer = new MutationObserver((mutations) => {
			if (
				hasPortalAttributeChange(mutations) ||
				hasRelevantChildListChange(mutations)
			) {
				updatePortalContainer();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-portal']
		});

		return () => observer.disconnect();
	}, []);

	return portalContainer;
};
